import { CircleNotch } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  getSignedPdfUrlResult,
  type SignedPdfUrlResult,
} from "../../../../../services/files";

const POLL_INTERVAL_MS = 3_000;
const MAX_POLL_MS = 60_000;

interface StillProcessingFallbackProps {
  url: string;
  fileName: string;
  onReady: (signedUrl: string) => void;
  onGiveUp: () => void;
}

export function StillProcessingFallback({
  url,
  fileName,
  onReady,
  onGiveUp,
}: StillProcessingFallbackProps) {
  const [retrying, setRetrying] = useState(false);
  const startedAtRef = useRef(Date.now());
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tryResolve = useCallback(async (): Promise<SignedPdfUrlResult> => {
    return getSignedPdfUrlResult(url);
  }, [url]);

  const handleResult = useCallback(
    (result: SignedPdfUrlResult) => {
      if (result.status === "ok") {
        onReady(result.url);
        return;
      }
      if (
        result.status === "not_ready" &&
        Date.now() - startedAtRef.current < MAX_POLL_MS
      ) {
        pollTimerRef.current = setTimeout(async () => {
          const next = await tryResolve();
          handleResult(next);
        }, POLL_INTERVAL_MS);
        return;
      }
      onGiveUp();
    },
    [onGiveUp, onReady, tryResolve],
  );

  useEffect(() => {
    startedAtRef.current = Date.now();
    void tryResolve().then(handleResult);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [handleResult, tryResolve]);

  const handleRetryClick = async () => {
    setRetrying(true);
    startedAtRef.current = Date.now();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    const result = await tryResolve();
    setRetrying(false);
    handleResult(result);
  };

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4"
      data-testid="file-preview-still-processing"
      aria-label="File is still processing"
    >
      <CircleNotch
        size={20}
        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
        aria-hidden="true"
      />
      <div className="flex-1">
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          {fileName}
        </Text>
        <Text as="p" size="1" className="text-puntt-neutral-gray-9">
          Still processing your file…
        </Text>
      </div>
      <Button
        data-testid="file-preview-still-processing-retry"
        size="1"
        variant="soft"
        aria-label="Retry loading file"
        disabled={retrying}
        onClick={() => void handleRetryClick()}
        className="cursor-pointer"
      >
        Retry
      </Button>
    </div>
  );
}
